import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './d-issue-message-thread.js';
import { t } from 'src/i18n';
import { fetchIssueMessages, IssueClosedError, postIssueMessage } from './issue-messages-client.js';
import type { IssueMessage } from './issue-message.js';

/**
 * Melderens innloggede meldingsside, rute `/account/{orgId}/issue-messages/{issueId}`.
 * Nås via lenken i «ny melding»-e-posten. Melder er anonym mot behandler (backend
 * matcher reporterRef mot innlogget bruker). Henter/sender via messages-endepunktet.
 */
@customElement('d-issue-messages-page')
export class DIssueMessagesPage extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: hsl(0, 0%, 97%);
      padding: 40px 20px;
      box-sizing: border-box;
      font-family: var(--mainSans, system-ui, sans-serif);
    }
    .frame {
      max-width: 680px;
      margin: 0 auto;
      background: white;
      border-radius: 12px;
      padding: 28px;
      box-shadow: 0 1px 8px hsla(0, 0%, 0%, 0.08);
    }
    h1 {
      font-size: 22px;
      margin: 0 0 4px;
      color: var(--pageHeaderColor, #1a1a1a);
    }
    .sub {
      font-size: 13px;
      color: hsla(0, 0%, 0%, 0.55);
      margin-bottom: 18px;
    }
    .error {
      font-size: 13px;
      color: hsl(0, 60%, 40%);
      margin-bottom: 10px;
    }
  `;

  @property({ type: String })
  organizationId = '';

  @property({ type: String })
  issueId = '';

  @state()
  private messages: IssueMessage[] = [];

  @state()
  private closed = false;

  @state()
  private error = '';

  private loadStarted = false;

  protected updated(changed: PropertyValues): void {
    super.updated(changed);
    if (!this.loadStarted && this.organizationId !== '' && this.issueId !== '') {
      this.loadStarted = true;
      void this.load();
    }
  }

  private async load(): Promise<void> {
    try {
      this.messages = await fetchIssueMessages(this.organizationId, this.issueId);
    } catch {
      this.error = t('issues.msgLoadErrorReporter');
    }
  }

  private async onSend(body: string): Promise<void> {
    try {
      await postIssueMessage(this.organizationId, this.issueId, body);
      this.messages = await fetchIssueMessages(this.organizationId, this.issueId);
      this.error = '';
    } catch (e) {
      if (e instanceof IssueClosedError) {
        this.closed = true;
        this.error = '';
      } else {
        this.error = t('issues.msgSendError');
      }
    }
  }

  render() {
    if (this.organizationId === '' || this.issueId === '') {
      return html`<div class="frame">${t('issues.msgMissingParams')}</div>`;
    }
    return html`
      <div class="frame">
        <h1>${t('issues.msgReporterTitle')}</h1>
        <div class="sub">${t('issues.msgReporterAnonymousNote')}</div>
        ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
        <d-issue-message-thread
          .messages=${this.messages}
          .viewer=${'reporter'}
          ?closed=${this.closed}
          @send=${(e: CustomEvent<{ body: string }>) => this.onSend(e.detail.body)}
        ></d-issue-message-thread>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-issue-messages-page': DIssueMessagesPage;
  }
}
